// @spec SCF-003
// @spec POL-STR-016
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function for merging Tailwind CSS classes.
 *
 * Combines clsx for conditional class handling with tailwind-merge
 * to intelligently merge Tailwind classes without conflicts.
 *
 * @example
 * cn('px-2', 'py-1') // 'px-2 py-1'
 * cn('px-2', undefined, 'py-1') // 'px-2 py-1'
 * cn('px-2', 'px-4') // 'px-4' (last wins)
 * cn('base', false && 'skip', 'end') // 'base end'
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format bytes to human-readable file size.
 *
 * @example
 * formatFileSize(1024) // '1 KB'
 * formatFileSize(1048576) // '1 MB'
 * formatFileSize(1073741824) // '1 GB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'] as const;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(1));
  const unit = sizes[i] ?? 'Bytes';

  return `${String(value)} ${unit}`;
}
